import { useCallback, useEffect, useRef, useState } from "react";
import { api, apiService } from "@/lib/api";
import { useVideoCall } from "@/hooks/useVideoCall";
import type { ConsultationCallSessionStatus } from "@/types/video-call";

interface UseConsultationEchoSessionOptions {
  appointmentId?: string;
}

interface KeepaliveRequestOptions {
  appointmentId: string;
}

const MAX_READY_NOTIFICATION_ATTEMPTS = 3;
const READY_NOTIFICATION_RETRY_DELAY_MS = 2000;

const sendKeepaliveStopRequest = async ({
  appointmentId,
}: KeepaliveRequestOptions) => {
  const token = localStorage.getItem("access_token");
  const baseURL = api.defaults.baseURL;

  if (!token || !baseURL) {
    throw new Error("Missing API base URL or access token");
  }

  const url = new URL(`/echo/appointments/${appointmentId}/stop/`, baseURL);

  await fetch(url.toString(), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ channel_name: appointmentId }),
    keepalive: true,
  });
};

export const useConsultationEchoSession = ({
  appointmentId,
}: UseConsultationEchoSessionOptions) => {
  const [sessionStatus, setSessionStatus] =
    useState<ConsultationCallSessionStatus>("idle");
  const [isInitializing, setIsInitializing] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);
  const [isRejoining, setIsRejoining] = useState(false);
  const [isVideoCallActive, setIsVideoCallActive] = useState(false);
  const [readyNotificationAttempt, setReadyNotificationAttempt] = useState(0);

  const latestAppointmentIdRef = useRef(appointmentId);
  const hasAnnouncedRecordingReadyRef = useRef(false);
  const hasAnnouncedRecordingStopRef = useRef(false);
  const readyNotificationRetryTimeoutRef = useRef<number | null>(null);

  const videoCall = useVideoCall({
    onCallEnd: () => {
      setSessionStatus((prev) => (prev === "ending" ? "ended" : prev));
    },
  });
  const {
    joinChannel,
    leaveChannel,
    localUserJoined,
    localUserId,
    remoteUserId,
  } = videoCall;

  useEffect(() => {
    latestAppointmentIdRef.current = appointmentId;
  }, [appointmentId]);

  const clearReadyNotificationRetry = useCallback(() => {
    if (readyNotificationRetryTimeoutRef.current !== null) {
      window.clearTimeout(readyNotificationRetryTimeoutRef.current);
      readyNotificationRetryTimeoutRef.current = null;
    }
  }, []);

  const resetSessionFlags = useCallback(() => {
    clearReadyNotificationRetry();
    hasAnnouncedRecordingReadyRef.current = false;
    hasAnnouncedRecordingStopRef.current = false;
    setReadyNotificationAttempt(0);
  }, [clearReadyNotificationRetry]);

  const joinConsultation = useCallback(async () => {
    if (!appointmentId) return;

    try {
      setIsInitializing(true);
      setInitError(null);
      setSessionStatus("idle");

      const response = await apiService.getVideoCallToken(appointmentId);
      const token = response.data?.token;

      if (!token) {
        throw new Error("Invalid token response from server");
      }

      await joinChannel(token, appointmentId);
      setIsVideoCallActive(true);
      setSessionStatus("armed");
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to start consultation";
      setInitError(errorMessage);
      setIsVideoCallActive(false);
      setSessionStatus("idle");
    } finally {
      setIsInitializing(false);
    }
  }, [appointmentId, joinChannel]);

  const stopEchoRecording = useCallback(
    async (mode: "interactive" | "keepalive" = "interactive") => {
      const activeAppointmentId = latestAppointmentIdRef.current;

      if (
        !activeAppointmentId ||
        hasAnnouncedRecordingStopRef.current ||
        !hasAnnouncedRecordingReadyRef.current
      ) {
        return;
      }

      hasAnnouncedRecordingStopRef.current = true;
      setSessionStatus("ending");

      try {
        if (mode === "keepalive") {
          await sendKeepaliveStopRequest({
            appointmentId: activeAppointmentId,
          });
        } else {
          await apiService.stopEchoRecording(activeAppointmentId, {
            channel_name: activeAppointmentId,
          });
        }
      } catch (error) {
        hasAnnouncedRecordingStopRef.current = false;
        setSessionStatus(
          hasAnnouncedRecordingReadyRef.current ? "recording" : "armed"
        );
        console.error("Failed to stop Echo recording", error);
      }
    },
    []
  );

  const startConsultation = useCallback(async () => {
    resetSessionFlags();
    await joinConsultation();
  }, [joinConsultation, resetSessionFlags]);

  const endConsultation = useCallback(async () => {
    try {
      await stopEchoRecording();
    } catch (err) {
      console.error("Failed to stop Echo recording during end:", err);
    }
    // Always release tracks and leave, even if recording stop failed
    await leaveChannel();
    setSessionStatus("ended");
    setIsVideoCallActive(false);
  }, [leaveChannel, stopEchoRecording]);

  const rejoinConsultation = useCallback(async () => {
    resetSessionFlags();
    setIsRejoining(true);

    try {
      await joinConsultation();
    } finally {
      setIsRejoining(false);
    }
  }, [joinConsultation, resetSessionFlags]);

  useEffect(() => {
    if (
      !appointmentId ||
      !isVideoCallActive ||
      hasAnnouncedRecordingReadyRef.current ||
      readyNotificationAttempt >= MAX_READY_NOTIFICATION_ATTEMPTS ||
      localUserId === null ||
      remoteUserId === null
    ) {
      return;
    }

    hasAnnouncedRecordingReadyRef.current = true;

    void apiService
      .notifyEchoParticipantsReady(appointmentId, {
        channel_name: appointmentId,
        provider_uid: String(localUserId),
        patient_uid: String(remoteUserId),
      })
      .then(() => {
        clearReadyNotificationRetry();
        setInitError(null);
        setSessionStatus("recording");
      })
      .catch((error) => {
        hasAnnouncedRecordingReadyRef.current = false;
        if (readyNotificationAttempt < MAX_READY_NOTIFICATION_ATTEMPTS - 1) {
          clearReadyNotificationRetry();
          readyNotificationRetryTimeoutRef.current = window.setTimeout(() => {
            setReadyNotificationAttempt((current) => current + 1);
          }, READY_NOTIFICATION_RETRY_DELAY_MS);
        } else {
          setInitError(
            "Echo recording could not be started. End and restart the consultation."
          );
        }
        console.error("Failed to notify Echo recording readiness", error);
      });
  }, [
    appointmentId,
    clearReadyNotificationRetry,
    isVideoCallActive,
    localUserId,
    remoteUserId,
    readyNotificationAttempt,
  ]);

  const notifyPatientToJoin = useCallback(async () => {
    if (!appointmentId) return;
    try {
      await apiService.notifyPatientToJoin(appointmentId);
    } catch (error) {
      console.error("Failed to notify patient", error);
    }
  }, [appointmentId]);

  useEffect(() => {
    if (isVideoCallActive && localUserJoined) {
      setSessionStatus((prev) => {
        if (prev === "idle") return "armed";
        return prev;
      });
    }
  }, [isVideoCallActive, localUserJoined]);

  useEffect(() => {
    const handlePageExit = () => {
      void stopEchoRecording("keepalive");
    };

    window.addEventListener("pagehide", handlePageExit);
    window.addEventListener("beforeunload", handlePageExit);

    return () => {
      window.removeEventListener("pagehide", handlePageExit);
      window.removeEventListener("beforeunload", handlePageExit);
      void stopEchoRecording();
    };
  }, [stopEchoRecording]);

  useEffect(() => {
    return () => {
      clearReadyNotificationRetry();
    };
  }, [clearReadyNotificationRetry]);

  return {
    ...videoCall,
    sessionStatus,
    isInitializing,
    initError,
    isRejoining,
    isVideoCallActive,
    startConsultation,
    endConsultation,
    rejoinConsultation,
    notifyPatientToJoin,
  };
};

export default useConsultationEchoSession;
